import React, { FC, ReactNode, useEffect, useRef, useState } from 'react';
import styles from './FloatButton.module.scss';

interface FloatButtonProps {
  /** 浮窗内容 */
  children: ReactNode;
  /** 初始距右边距，默认 16 */
  defaultRight?: number;
  /** 初始距底部距离，默认 80 */
  defaultBottom?: number;
  /** 是否显示关闭按钮，默认 false */
  closable?: boolean;
  /** 松手后是否自动吸附到左/右边缘，默认 true */
  snap?: boolean;
  /** 吸附/拖拽时左边缘最小间距（px），默认 16 */
  snapLeftGap?: number;
  /** 吸附/拖拽时右边缘最小间距（px），默认 16 */
  snapRightGap?: number;
  /** 吸附/拖拽时顶部最小间距（px），默认 16 */
  snapTopGap?: number;
  /** 吸附/拖拽时底部最小间距（px），默认 16 */
  snapBottomGap?: number;
  /** 关闭后的回调 */
  onClose?: () => void;
  /** 额外的 className */
  className?: string;
}

const FloatButton: FC<FloatButtonProps> = (props) => {
  const {
    children,
    defaultRight = 16,
    defaultBottom = 80,
    closable = false,
    snap = true,
    snapLeftGap = 16,
    snapRightGap = 16,
    snapTopGap = 16,
    snapBottomGap = 16,
    onClose,
    className,
  } = props;

  // 关闭状态（storageKey 存在时读取 sessionStorage，刷新后重置）
  const [closed, setClosed] = useState(false);

  // 拖拽位置（right/bottom 相对视口）
  const [position, setPosition] = useState({ right: defaultRight, bottom: defaultBottom });

  // 是否正在执行吸附动画（开启 transition）
  const [snapping, setSnapping] = useState(false);

  const wrapRef = useRef<HTMLDivElement>(null);
  const isDraggingRef = useRef(false);
  const dragStartRef = useRef<{ mouseX: number; mouseY: number; right: number; bottom: number } | null>(null);
  // 记录是否发生了真实拖动（超过阈值），区分点击与拖拽
  const hasDraggedRef = useRef(false);
  const positionRef = useRef(position);
  positionRef.current = position;

  // 保存当次拖拽的事件监听器引用，以便组件卸载时能强制清理
  const cleanupDragListenersRef = useRef<(() => void) | null>(null);

  // 组件卸载时清理可能残留的 document 事件监听器，防止内存泄漏
  useEffect(() => {
    return () => {
      cleanupDragListenersRef.current?.();
    };
  }, []);

  // 关闭状态仅保存在内存中，刷新页面后自动重置，重新展示浮窗
  const handleClose = () => {
    setClosed(true);
    onClose?.();
  };

  /**
   * 松手后执行吸附
   * 直接读取 rect 实时位置，不依赖 positionRef，避免一帧差导致左间距丢失
   */
  const doSnap = () => {
    const el = wrapRef.current;
    if (!el) return;

    const rect = el.getBoundingClientRect();
    const elWidth = rect.right - rect.left;
    const elHeight = rect.bottom - rect.top;
    // clientWidth/clientHeight 不含滚动条，与 fixed 定位的坐标系一致
    const winW = document.documentElement.clientWidth;
    const winH = document.documentElement.clientHeight;

    // elWidth 异常时不执行吸附，避免跑飞
    if (elWidth <= 0 || elHeight <= 0) return;

    // 用 rect.left 直接判断吸附方向（最可靠）
    const centerX = rect.left + elWidth / 2;
    const snapToRight = centerX >= winW / 2;

    // 吸附到右边：right = snapRightGap
    // 吸附到左边：left = snapLeftGap → right = winW - elWidth - snapLeftGap
    const snappedRight = snapToRight ? snapRightGap : winW - elWidth - snapLeftGap;

    // 上下限制：距顶部 >= snapTopGap，距底部 >= snapBottomGap
    // bottom 是距视口底部的距离，bottom = winH - rect.bottom
    const currentBottom = winH - rect.bottom;
    const clampedBottom = Math.max(snapBottomGap, Math.min(currentBottom, winH - elHeight - snapTopGap));

    setSnapping(true);
    setPosition({ right: snappedRight, bottom: clampedBottom });
  };

  const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    // 点击关闭按钮时不触发拖拽
    if ((e.target as HTMLElement).closest(`.${styles['float-btn-close']}`)) return;

    e.preventDefault();
    // 若上一次拖拽未正常结束（如快速连按），先清理残留监听器，防止泄漏
    cleanupDragListenersRef.current?.();
    isDraggingRef.current = true;
    hasDraggedRef.current = false;
    // 拖拽期间关闭吸附动画
    setSnapping(false);

    // mousedown 时缓存元素尺寸，mousemove 中直接使用，避免每帧重复调用 getBoundingClientRect
    const initRect = wrapRef.current?.getBoundingClientRect();
    const elW = initRect ? initRect.right - initRect.left : 120;
    const elH = initRect ? initRect.bottom - initRect.top : 120;

    dragStartRef.current = {
      mouseX: e.clientX,
      mouseY: e.clientY,
      right: positionRef.current.right,
      bottom: positionRef.current.bottom,
    };

    const onMouseMove = (moveEvent: MouseEvent) => {
      if (!isDraggingRef.current || !dragStartRef.current) return;

      const dx = moveEvent.clientX - dragStartRef.current.mouseX;
      const dy = moveEvent.clientY - dragStartRef.current.mouseY;

      // 移动超过 3px 才视为拖拽
      if (Math.abs(dx) > 3 || Math.abs(dy) > 3) {
        hasDraggedRef.current = true;
      }

      const newRight = dragStartRef.current.right - dx;
      const newBottom = dragStartRef.current.bottom - dy;

      // 拖拽时限制在视口范围内，左右分别使用各自的边距
      // right 范围：[snapRightGap, winW - elW - snapLeftGap]
      // bottom 范围：[snapBottomGap, winH - elH - snapTopGap]
      // clientWidth/clientHeight 不含滚动条，与 fixed 定位的坐标系一致
      const vw = document.documentElement.clientWidth;
      const vh = document.documentElement.clientHeight;
      const clampedRight = Math.max(snapRightGap, Math.min(newRight, vw - elW - snapLeftGap));
      const clampedBottom = Math.max(snapBottomGap, Math.min(newBottom, vh - elH - snapTopGap));

      setPosition({ right: clampedRight, bottom: clampedBottom });
    };

    // 用对象持有两个函数引用，解开 onMouseUp ↔ cleanup 的循环依赖
    const listeners = { onMouseUp: () => {} };

    // 统一清理函数：移除监听器并置空 ref
    // onMouseUp 与组件卸载 effect 均通过此函数清理，保证单一职责
    const cleanup = () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', listeners.onMouseUp);
      cleanupDragListenersRef.current = null;
    };

    const onMouseUp = () => {
      isDraggingRef.current = false;
      dragStartRef.current = null;
      cleanup();

      // 松手后触发吸附，直接用 rect 实时值，不传 positionRef
      if (snap && hasDraggedRef.current) {
        doSnap();
      }
    };
    listeners.onMouseUp = onMouseUp;

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
    // 将清理函数存入 ref，以便组件卸载时兜底调用
    cleanupDragListenersRef.current = cleanup;
  };

  if (closed) return null;

  return (
    <div
      ref={wrapRef}
      className={`${styles['float-btn-wrap']} ${snapping ? styles['float-btn-snapping'] : ''} ${className || ''}`}
      style={{ right: position.right, bottom: position.bottom }}
      onMouseDown={handleMouseDown}
    >
      {/* 关闭按钮：仅 closable=true 时渲染 */}
      {closable && (
        <button className={styles['float-btn-close']} onClick={handleClose} aria-label="关闭浮窗">
          <svg width="10" height="10" viewBox="0 0 10 10" fill="none" aria-hidden="true">
            <path d="M1 1l8 8M9 1L1 9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
          </svg>
        </button>
      )}
      {children}
    </div>
  );
};

FloatButton.displayName = 'FloatButton';

export default FloatButton;
